import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import AppErrorBoundary from "./AppErrorBoundary";
import { useAuthFetch } from "../utils/useAuthFetch";
import { createFrontendErrorReporter } from "../services/frontendErrorReporting";

interface Props {
  children: ReactNode;
}

function asError(value: unknown): Error {
  if (value instanceof Error) return value;
  return new Error(
    typeof value === "string" ? value : "Unhandled promise rejection",
  );
}

const AuthenticatedErrorReporting: React.FC<Props> = ({ children }) => {
  const authFetch = useAuthFetch();
  const report = useMemo(
    () => createFrontendErrorReporter(authFetch),
    [authFetch],
  );
  const onBoundaryError = useCallback(
    (error: Error, componentStack: string) =>
      report(error, componentStack, "app_boundary"),
    [report],
  );

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      report(
        event.error ?? new Error(event.message || "Unhandled window error"),
        "",
        "window_error",
      );
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      report(asError(event.reason), "", "unhandled_rejection");
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [report]);

  return (
    <AppErrorBoundary onReport={onBoundaryError}>{children}</AppErrorBoundary>
  );
};

export default AuthenticatedErrorReporting;
